/**
 * GJESTEN (sak 232, PLAN 1.3): en person med en levende deling av typen `external` i en organisasjon
 * hen ikke er medlem av. Gjesten åpner det delte DER DET LIGGER, med vanlige sider og tabeller.
 *
 * Gjestens rettigheter er ikke en kolonne i Rettigheter — gjesten har ingen rolle i huset. De er
 * denne faste hvitlista, kumulativ per nivå (se < endre), og hver post må være dekket
 * av en deling på riktig nivå. RLS sier det samme i basen (`app.guest_paths`, 0458): `can()` og
 * basen svarer hver for seg, og begge må si ja.
 *
 * ØKONOMI OG KONTRAKT DELES ALDRI (232.10), uansett nivå. Heller ikke deling videre, innstillinger,
 * invitasjoner, medlemskap og sletting av programpost, avdeling og årgang (232.3) — `GUEST_NEVER`.
 */
import type { GrantPermission } from '../enums.js';
import type { EditionId, Uuid } from '../ids.js';
import type { Action, Actor, GuestScope, PolicyResult, RecordResource, Resource } from './types.js';

const VIEW_ACTIONS: readonly Action[] = [
  'festival:list',
  'festival:read',
  'edition:list',
  'edition:read',
  'stage:list',
  'department:list',
  'department:read',
  'performance:list',
  'performance:read',
  'shift:list',
  'resource_type:list',
  'resource:list',
  'allocation:list',
  'contact:list',
  'contact:read',
  'person:read',
  /* Personlista: årgangens folk og deltakerne (People-siden spør begge). */
  'crew_member:list',
  'participation:list',
  'document:list',
  'document:read',
  'task:list',
  'task:read',
  'thread:list',
  'thread:read',
  'message:list',
  /* Sine egne delinger — `object_grants` gir bare gjestens egne rader. */
  'object_grant:list',
  /*
   * OPPSLAGSLISTENE DE VANLIGE SIDENE HENTER UTEN Å SPØRRE (P5, sak 232 T5): årgangens kalenderstripe,
   * akkrediteringsnivåene, deltakerrollene og personfeltene. Uten dem svarte Oversikt og Personer 403
   * for en gjest. RLS snevrer hver av dem (0464): nivåene i årgangen, rollene og feltene til de synlige
   * deltakerne, og kalenderen til det delte — `calendar_events` har ingen gjestesti.
   */
  'event:list',
  'accreditation_level:list',
  'participation_role:list',
  'person_field:list',
  /*
   * EKSPORT (docs/EKSPORT.md §6): «Eksterne med innlogging kan eksportere det som er delt med dem.»
   * Katalogens `guest` og de vanlige cellene avgjør hvilke eksporter; RLS avgjør innholdet.
   */
  'export:create',
  /*
   * DEN SOM SER, KAN SKRIVE I CHATTEN (bestillingen §5.2, 27.10.2026). «Kan kommentere» er borte for
   * alle eksterne: det finnes bare se og endre. Chatten på et delt objekt, delingstråden og
   * kommentarene på en delt oppgave følger av å se det. Basen sier det samme (1100): innleggene i
   * `messages`, `thread_participants` og `task_comments` krever `view`, ikke et nivå over.
   */
  'performance:comment',
  'task:comment',
  'message:create',
];

const EDIT_ACTIONS: readonly Action[] = [
  /* Å fullføre en delt oppgave er en endring av den (§5.2). En oppgave gjesten er GITT, kan fullføres likevel. */
  'task:complete',
  'performance:update',
  'shift:create',
  /* Å sette folk på vakt og å knytte en tildeling til vakta er `shift:update` (people.controller). */
  'shift:update',
  'shift:delete',
  'resource:create',
  'resource:update',
  /* 232.3: «lage, endre og slette vakter og ressurser i avdelingen». Basen: `delete_sql` (0467). */
  'resource:delete',
  /* Reservasjonene. */
  'allocation:create',
  'allocation:update',
  'allocation:delete',
  'task:update',
];

/** Kumulativt: `edit` er alt `view` kan pluss sitt eget. */
export const GUEST_ACTIONS: Readonly<Record<GrantPermission, readonly Action[]>> = Object.freeze({
  view: Object.freeze([...VIEW_ACTIONS]),
  edit: Object.freeze([...VIEW_ACTIONS, ...EDIT_ACTIONS]),
});

/** Ressursene en gjest aldri når, uansett verb og nivå. */
const NEVER_RESOURCES: ReadonlySet<string> = new Set([
  'economy',
  'contract',
  'artist_agreement',
  'concert_budget',
  'venue_rate',
  'date_request',
  'agency_move',
  'external',
  'share',
  'share_link',
  'invitation',
  'membership',
  'member_profile',
  'role_permission',
  'organization',
  'audit_log',
]);

/** Å slette det delte selv (232.3: «gir ikke rett til å slette avdelingen»). */
const NEVER_DELETE: ReadonlySet<string> = new Set(['performance', 'department', 'edition']);

/** Aldri for en gjest: økonomi, kontrakt, deling videre, innstillinger og sletting av det delte. */
export function isGuestNever(action: Action): boolean {
  const [resource = '', verb = ''] = action.split(':');
  if (NEVER_RESOURCES.has(resource)) return true;
  if (verb === 'share' || verb === 'manage_roles' || verb === 'access_all') return true;
  return verb === 'delete' && NEVER_DELETE.has(resource);
}

export const GUEST_NEVER = Object.freeze({
  resources: [...NEVER_RESOURCES].sort(),
  deletes: [...NEVER_DELETE].sort(),
  verbs: ['access_all', 'manage_roles', 'share'],
});

const RANK: Readonly<Record<GrantPermission, number>> = { view: 0, edit: 1 };

const higher = (
  a: GrantPermission | null,
  b: GrantPermission | undefined,
): GrantPermission | null => (b === undefined ? a : a === null || RANK[b] > RANK[a] ? b : a);

/** Det laveste nivået som tillater handlingen, eller null når ingen gjest kan gjøre den. */
export function guestLevelFor(action: Action): GrantPermission | null {
  if (GUEST_ACTIONS.view.includes(action)) return 'view';
  if (GUEST_ACTIONS.edit.includes(action)) return 'edit';
  return null;
}

const isLive = (expiresAt: string | null): boolean =>
  expiresAt === null || new Date(expiresAt).getTime() > Date.now();

/**
 * Nivået gjesten har på DENNE posten: en direkte deling av samme type og id, en delt årgang
 * (`editionId`, eller posten er årgangen), eller en delt avdeling (`departmentId`/`departmentIds`,
 * eller posten er avdelingen). `departments` er allerede utvidet til underavdelinger og til
 * avdelingene i en delt årgang, så en vakt i «Område › Inngang» er dekket av en deling av «Område».
 */
export function guestLevelOn(scope: GuestScope, record: RecordResource): GrantPermission | null {
  let level: GrantPermission | null = null;
  for (const grant of scope.grants) {
    if (
      grant.objectType === record.type &&
      grant.objectId === record.id &&
      isLive(grant.expiresAt)
    ) {
      level = higher(level, grant.permission);
    }
  }
  const edition = (id: Uuid | EditionId | null | undefined): void => {
    if (id !== null && id !== undefined) level = higher(level, scope.editions.get(id as Uuid));
  };
  const department = (id: Uuid | null | undefined): void => {
    if (id !== null && id !== undefined) level = higher(level, scope.departments.get(id));
  };
  /*
   * En oppgave er dekket bare av en deling av oppgaven selv (eller av at den er gitt gjesten, i
   * `guestCan`) — aldri av årgangen eller avdelingen den står i. Samme regel som `guest_task_ids`
   * (0466): husets oppgaveliste følger ikke med en deling.
   */
  if (record.type === 'task') return level;
  /* §5.3 (1102): det en delt scene når — programpostene, vaktene, ressursen og reservasjonene. */
  level = higher(level, scope.reach?.get(`${record.type}:${record.id}`));
  if (record.type === 'edition') edition(record.id);
  edition(record.editionId);
  if (record.type === 'department') department(record.id);
  department(record.departmentId);
  for (const id of record.departmentIds ?? []) department(id);
  return level;
}

/**
 * FANENE GJESTEN SER PÅ ÉN PROGRAMPOST — SUMMEN AV DELINGENE (239.30).
 *
 * Før vant den mest spesifikke delingen: Ella hadde hele årgangen, men én programpost som også var
 * delt direkte med omfanget Oversikt/Personer/Chat, viste bare de tre fanene. Atle 04.10: summen
 * gjelder, og skal noe skjules, må den brede delingen snevres inn. En delt årgang er derfor hele
 * posten (`null`); ellers gjelder omfanget på den direkte delingen, og `null` uten noen.
 *
 * 246.3: MEN BARE DER ÅRGANGEN NÅR POSTEN. En årgangsdeling tar bare med bekreftede poster (242.2);
 * en ubekreftet post delt direkte og smalt viste likevel alle fanene. Når delingen sier
 * `beyondEdition`, gjelder omfanget dens uansett årgangen.
 */
export function guestPerformanceScopes(
  actor: Actor,
  performanceId: string,
  editionId: string | null | undefined,
): readonly string[] | null {
  if (actor.orgRole !== 'guest' || !actor.guest) return null;
  const grant = actor.guest.grants.find(
    (g) => g.objectType === 'performance' && g.objectId === performanceId && isLive(g.expiresAt),
  );
  const throughEdition =
    editionId != null &&
    actor.guest.editions.has(editionId as Uuid) &&
    grant?.beyondEdition !== true;
  if (throughEdition) return null;
  if (grant !== undefined) return grant.scopes;
  /*
   * §5.3: BARE GJENNOM EN DELT SCENE — tidene og den tekniske advancen (`shareScopeAllows` og 1102).
   * En avdelingsdeling som også når posten, gir hele posten som før.
   */
  const { reach: _reach, ...withoutStage } = actor.guest;
  const record: RecordResource = {
    kind: 'record',
    type: 'performance',
    id: performanceId as Uuid,
    organizationId: actor.organizationId,
    ...(editionId == null ? {} : { editionId: editionId as EditionId }),
  };
  if (
    actor.guest.reach?.has(`performance:${performanceId}`) === true &&
    guestLevelOn(withoutStage, record) === null
  ) {
    return STAGE_PERFORMANCE_SCOPES;
  }
  return null;
}

/** Fanene en programpost viser når den bare nås gjennom en delt scene (§5.3). */
export const STAGE_PERFORMANCE_SCOPES: readonly string[] = Object.freeze(['overview', 'advance']);

/** Det høyeste nivået gjesten har noe sted i huset — samlingens spørsmål. */
function highestLevel(scope: GuestScope | null | undefined): GrantPermission | null {
  if (!scope) return null;
  let level: GrantPermission | null = null;
  for (const grant of scope.grants) {
    if (isLive(grant.expiresAt)) level = higher(level, grant.permission);
  }
  for (const permission of scope.departments.values()) level = higher(level, permission);
  for (const permission of scope.editions.values()) level = higher(level, permission);
  for (const permission of scope.reach?.values() ?? []) level = higher(level, permission);
  return level;
}

/**
 * LESING AV SAMMENHENGEN det delte står i: festivalen, årgangen, avdelingens navn, kontaktkortet,
 * dokumentet og oppgaven (tråden har sin egen liste, se `guestCan`). Posten kom gjennom resolveren
 * INNE i transaksjonen, altså gjennom gjestevakten i basen — den er synlig fordi basen sa ja. Å kreve en deling på selve posten her
 * ville gitt 404 på navnet til årgangen en delt vakt står i.
 */
const CONTEXT_READS: ReadonlySet<Action> = new Set<Action>([
  'festival:read',
  'edition:read',
  'department:read',
  'contact:read',
  'person:read',
  'document:read',
  'task:read',
]);

const EVERYTHING: PolicyResult = { allowed: true, scope: { kind: 'all' } };

/**
 * `can()` for en gjest (steg 1 er allerede tatt: posten ligger i huset). Matrisen leses aldri.
 *
 * En SAMLING tillates med `{ kind: 'all' }` — RLS snevrer lista til det delte — når gjesten har et
 * nivå som tillater handlingen noe sted. En POST må være dekket på riktig nivå; ellers `grant_missing`
 * eller `grant_insufficient`, som begge er 404 (`HIDE_EXISTENCE`).
 */
export function guestCan(actor: Actor, action: Action, resource: Resource): PolicyResult {
  if (isGuestNever(action)) return { allowed: false, reason: 'guest_never' };
  const needed = guestLevelFor(action);
  if (needed === null) return { allowed: false, reason: 'role_not_permitted' };

  if (resource.kind === 'collection') {
    if (needed === 'view') return EVERYTHING;
    const best = highestLevel(actor.guest);
    return best !== null && RANK[best] >= RANK[needed]
      ? EVERYTHING
      : { allowed: false, reason: 'grant_insufficient' };
  }

  const scope = actor.guest;
  if (!scope) return { allowed: false, reason: 'grant_missing' };

  /*
   * EN TRÅD ER GJESTENS BARE NÅR EN DELING NÅR DEN (0469): delingstråden, eller chatten på en delt
   * programpost eller oppgave — på nivået til den delingen, og aldri en intern tråd på objektet.
   * `scope.threads` er den samme lista basen leser (`guest_thread_ids`). Ikke årgangen tråden står
   * i (den ville gitt årgangsdelingens nivå på hver tråd i årgangen), ikke det høyeste nivået i
   * organisasjonen, og ikke `CONTEXT_READS`: før 0469 var basen den eneste låsen her.
   */
  if (resource.type === 'thread') {
    const reached = scope.threads?.get(resource.id) ?? null;
    if (reached === null) return { allowed: false, reason: 'grant_missing' };
    return RANK[reached] >= RANK[needed]
      ? EVERYTHING
      : { allowed: false, reason: 'grant_insufficient' };
  }

  let level = guestLevelOn(scope, resource);

  /*
   * DEN TEKNISKE ADVANCEN PÅ EN DELT SCENE (§5.3): «Kan endre» på scenen gir å endre teknisk advance
   * på programpostene der — ikke posten selv, og ikke de andre seksjonene. Resolveren sier hvilken
   * seksjon skrivingen gjelder (`advanceSection`); basen sier det samme (`guest_stage_section_ids`).
   */
  if (resource.type === 'performance' && resource.advanceSection === 'technical') {
    level = higher(level, scope.stageTechnical?.get(resource.id));
  }

  /*
   * Oppgaven gjesten er GITT: delingen står ikke på posten, men basen har allerede avgjort nivået
   * (`guest_task_ids`). Her holder det at gjesten er ansvarlig eller medansvarlig.
   */
  if (level === null && resource.type === 'task') {
    const given =
      resource.assigneePersonId === actor.personId ||
      resource.participantPersonIds?.includes(actor.personId) === true;
    /*
     * En gitt oppgave kan leses, kommenteres og fullføres — ikke endres (samme som `guest_task_ids`
     * og `guest_given_task_ids`, 1100). Fullføringen er det ene unntaket fra nivåene.
     */
    if (given && action === 'task:complete') return EVERYTHING;
    if (given) level = 'view';
  }

  if (level === null) {
    return needed === 'view' && CONTEXT_READS.has(action)
      ? EVERYTHING
      : { allowed: false, reason: 'grant_missing' };
  }
  return RANK[level] >= RANK[needed]
    ? EVERYTHING
    : { allowed: false, reason: 'grant_insufficient' };
}
